import React, { useState } from 'react'
import { StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native'
import {
  Button,
  Checkbox,
  Icon,
  PaginationItem,
  Spacing,
  Table,
  TableCol,
  TableSort,
  Text,
  TextInput,
} from 'zens-storybook'
import { Trans, useTranslation } from 'react-i18next'
import { useWorkspaceTypes, useWorkspaces, Workspace, Zone } from 'zens-store'

import { FloorCell } from '../floor'
import { TablePaginated } from '../table'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    overflow: 'scroll',
  },
  title: {
    marginBottom: Spacing.S,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: Spacing.XS,
  },
  search: {
    minWidth: 300,
  },
  footer: {
    marginTop: Spacing.M,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  buttons: {
    flexDirection: 'row',
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  skeletonSearch: {
    minWidth: 200,
  },
})

interface Data {
  data: Workspace
  onPress?: () => void
}

interface Props {
  zone?: Zone
  companyId: number
  onCancel?: () => void
  onSave?: (workspaces: Workspace[]) => void
}
const ZoneWorkspacesTable = (props: Props) => {
  const { companyId, zone, onCancel, onSave } = props
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Record<number, Workspace>>({})
  const [pagination, setPagination] = useState({ page: 0, size: 10 })
  const [sort, setSort] = useState<TableSort>({ field: 'name', direction: 'asc' })
  const { t } = useTranslation()
  const zoneId = zone?.id || 0
  const buildingId = zone?.buildingId

  const workspaces = useWorkspaces({
    companyId,
    filter: {
      zoneId: [zoneId],
      ...(buildingId ? { buildingId: [buildingId] } : {}),
      search,
    },
    notInZone: true,
    pagination,
    sort,
  })

  const types = useWorkspaceTypes()
  const data: Data[] =
    workspaces.data?.data.map((workspace) => ({
      data: workspace,
      onPress: () => toggle(workspace),
      accessory: false,
    })) || []

  function updateSearch(search: string) {
    setSearch(search)
    setPagination({ page: 0, size: pagination.size })
  }

  const toggle = (workspace: Workspace) => {
    if (selected[workspace.id]) {
      const newSelected = { ...selected }
      delete newSelected[workspace.id]
      setSelected(newSelected)
    } else {
      setSelected({ ...selected, [workspace.id]: workspace })
    }
  }

  const selectAll = (value: boolean) => {
    if (value) {
      setSelected(
        data.reduce((acc, item) => ({ ...acc, [item.data.id]: item.data }), {
          ...selected,
        }),
      )
    } else {
      const newSelected = { ...selected }
      data.forEach((item) => delete newSelected[item.data.id])
      setSelected(newSelected)
    }
  }

  const save = () => {
    onSave?.(Object.values(selected))
  }
  const renderHeader = () => {
    if (data?.length)
      return (
        <View style={styles.header}>
          <TextInput
            left="search"
            placeholder={t('Search workspace name')}
            right={
              search ? (
                <TouchableOpacity onPress={() => updateSearch('')} testID="WorkspaceList.Search.Clear">
                  <Icon name="close" size="S" />
                </TouchableOpacity>
              ) : undefined
            }
            value={search}
            onChangeText={updateSearch}
            style={styles.search}
            testID="WorkspaceList.Search"
          />
        </View>
      )
  }

  const renderSelect = () => {
    return (
      <Checkbox
        value={
          Object.keys(selected).filter((item) => !!data.find((workspace) => `${workspace.data.id}` === item)).length > 0
        }
        checkmark={<Icon name="minus" color="white" size="XS" />}
        onValueChange={selectAll}
        testID="Workspaces.SelectAll"
      />
    )
  }

  const renderType = (workspace: Workspace) => {
    const { workspaceTypeId } = workspace
    const type = types.data?.find((item) => item.id === workspaceTypeId)
    return <Text>{type?.name}</Text>
  }

  const renderFloor = (workspace: Workspace) => {
    const { floorId } = workspace
    return <FloorCell companyId={companyId} floorId={floorId || NaN} />
  }

  const cols: TableCol<Workspace>[] = [
    {
      key: 'select',
      title: renderSelect(),
      sort: false,
      render: (workspace: Workspace) => <Checkbox disabled value={!!selected[workspace.id]} />,
      size: {
        width: 30,
      },
    },
    {
      key: 'name',
      title: t('Workspace name'),
    },
    {
      key: 'workspaceTypeId',
      title: t('Workspace type'),
      render: renderType,
    },
    {
      key: 'floor',
      title: t('Floor'),
      render: renderFloor,
    },
  ]

  const numberOfWorkspaces = Object.keys(selected).length || 0
  return (
    <View style={styles.container}>
      <Text level={4} style={styles.title}>
        <Trans>Add workspaces to zone</Trans>
      </Text>
      {renderHeader()}
      <TablePaginated
        style={styles.content}
        rows={data}
        cols={cols}
        sort={sort}
        onSortChange={setSort}
        dataIsSorted
        pagination={pagination}
        count={workspaces.data?.meta?.total || 0}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: workspaces.data?.meta?.page,
            numberOfPages: Math.ceil((workspaces.data?.meta?.total || 0) / (workspaces.data?.meta?.size || 0)) || 0,
          })
        }
        onPaginationChange={setPagination}
        paginationItems={PaginationItem.Count | PaginationItem.Buttons}
      />

      <View style={styles.footer}>
        <Text level={5}>
          <Trans>Selected workspaces</Trans> ({numberOfWorkspaces})
        </Text>
        <View style={styles.buttons}>
          <Button title={t('Cancel')} size="L" light style={styles.button} onPress={onCancel} />
          <Button title={t('Add selection')} size="L" style={styles.button} onPress={save} testID="Workspaces.Save" />
        </View>
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()

  const cols: TableCol<Workspace>[] = [
    {
      key: 'select',
      title: ' ',
      size: {
        width: 30,
      },
      sort: false,
    },
    {
      key: 'name',
      title: t('Workspace name'),
    },
    {
      key: 'workspaceTypeId',
      title: t('Workspace type'),
    },
    {
      key: 'floorId',
      title: t('Floor'),
    },
  ]
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Text level={4} style={styles.title}>
        {t('Add workspaces to zone')}
      </Text>
      <View style={styles.header}>
        <TextInput.Skeleton style={styles.skeletonSearch} />
      </View>
      <Table.Skeleton style={styles.content} cols={cols} />
      <View style={styles.footer}>
        <Text.Skeleton level={5} />
        <View style={styles.buttons}>
          <Button.Skeleton size="L" style={styles.button} />
          <Button.Skeleton size="L" style={styles.button} />
        </View>
      </View>
    </View>
  )
}
ZoneWorkspacesTable.Skeleton = Skeleton

export default ZoneWorkspacesTable
